import React, { useEffect, useState } from "react";
import { useAuth } from "../auth/FirebaseAuth.js";
import { getUserById } from "../models/UserFunctions.js"; // Function to fetch user data
import {
  getOrgByRef,
  transformClientsToTable,
  transformPartnersToTable,
} from "../models/OrgFunctions.js"; // Functions to fetch org and client data
import Column from "../layout/Column.js";
import Row from "../layout/Row.js";
import Divider from "../layout/Divider.js";
import Space from "../layout/Space.js";
import DataGridFull from "../tables/DataGridFull.js";
import { useTheme } from "@mui/material";
import PrimaryButton from "../buttons/PrimaryButton.js";
import Icon1 from "../icons/Icon.js";
import { Skeleton } from "@mui/material";
import TFGPT1 from "../textfields/TFGPT1.js";
import {
  getArrayDocDataByID,
  queryCollection,
} from "../models/DataFunctions.js";
import checkPageAccess, { checkPageAccessV2 } from "../auth/checkPageAccess.js";
import ClientTabs from "../tabs/ClientTabs.js";
import { useSnackbar } from "notistack";
import TabGPT from "../tabs/TabGPT.js";
import SecondaryButton from "../buttons/SecondaryButton.js";
import EmptyDisplay1 from "../modals/EmptyDisplay1.js";
import Gleap from "gleap";
import { Spin } from "antd";
import DataGridGPT2 from "../tables/DataGridGPT2.js";
import { hasAudit, hasInfiltro } from "../hexagone/VisibilityHelpers.js";
import SmallSpinner from "../loading/SmallSpinner.js";
import ExtrairePartnersModal from "../modals/ExtrairePartnersModal.js";

function Partners({ userData, orgData, navigateWorkspace, workspaceData }) {
  const theme = useTheme();
  const { currentUser, loading } = useAuth();
  const [loadingRows, setLoadingRows] = useState(true);

  const [searchValue, setSearchValue] = useState("");
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [options, setOptions] = useState([]);
  const [tabs = [], setTabs] = useState([]);

  const { enqueueSnackbar } = useSnackbar();
  const handleClickVariant = (message, variant) => {
    enqueueSnackbar(message, { variant });
  };

  const [allWorkspaces, setAllWorkspaces] = useState([]);

  const [allClients, setAllClients] = useState([]);
  const [allPartners, setAllPartners] = useState([]);
  const tabContent = [<span></span>, <span></span>, <span></span>];
  const [activetab, setActivetab] = useState("all");
  const [extractOpen, setExtractOpen] = useState(false);

  // Function to fetch user data by ID

  // Function to navigateWorkspace to a client by ID
  const gotoClient = (parnter) => {
    navigateWorkspace(`/partner/${parnter.id}`);
  };

  const gotoNewClient = () => {
    navigateWorkspace(`/newpartner`);
  };

  // Function to navigateWorkspace to a client by name
  const gotoClientName = (value) => {
    // If client exists, navigateWorkspace to the client page by ID
    if (value.value) {
      navigateWorkspace(`/partner/${value.value}`);
    } else {
    }
  };

  // Function to fetch data for the current user
  const handleFetchData = async () => {
    if (userData) {
      try {
        // Fetch user data by currentUser's ID
        if (
          (await checkPageAccessV2(
            userData,
            orgData,
            workspaceData,
            "basic"
          )) == false
        ) {
          console.error("Access denied to Partners page");
          navigateWorkspace("/home");
        }
        // Parallel fetching of organization and clients
        const partners = await queryCollection("org", [
          {
            field: "masters_id",
            operator: "array-contains",
            value: userData.org_id,
          },
        ]);
        const workspaces_ids = partners
          .map((partner) => partner.workspaces_id || [])
          .flat();
        let all_workspaces = await getArrayDocDataByID(
          "workspace",
          workspaces_ids
        );
        all_workspaces = all_workspaces.filter(
          (workspace) => workspace && workspace.master_id === userData.org_id
        );
        setAllWorkspaces(all_workspaces);

        setTabs([
          {
            label: "Tous",
            id: "all",
          },
          hasAudit(orgData) && {
            id: "travaux",
            label: "Entreprise de travaux",
          },
          hasAudit(orgData) && {
            id: "agence_immobilier",
            label: "Agence immobilière",
          },
          hasAudit(orgData) && { id: "mar", label: "MAR" },

          hasInfiltro(orgData) && {
            label: "Architectes",
            id: "architect",
          },
          hasInfiltro(orgData) && {
            label: "Artisans",
            id: "artisan",
          },
          hasInfiltro(orgData) && {
            label: "Constructeurs",
            id: "constructor",
          },
          hasInfiltro(orgData) && {
            label: "Promoteurs",
            id: "promotor",
          },
          hasInfiltro(orgData) && {
            label: "Maîtres d'oeuvre",
            id: "mdo",
          },
          hasInfiltro(orgData) && {
            label: "Dessinateurs",
            id: "drawer",
          },
          hasInfiltro(orgData) && {
            label: "Bureaux d'étude",
            id: "expert",
          },
          hasInfiltro(orgData) && {
            label: "Infiltromètres",
            id: "infiltro",
          },
          {
            label: "Prospects",
            id: "prospect",
          },
          {
            label: "Autres",
            id: "other",
          },
        ]);

        let tab = "all";

        // if no clients in constructor and one in architect, set active tab to architect

        // Transform clients into table format (rows and columns)
        const { rows, columns } = transformPartnersToTable(
          partners,
          all_workspaces,
          tab
        );

        // Update state if the user data is new or changed
        // Batch state updates to minimize re-renders
        setAllPartners(partners);
        setRows(rows);
        setColumns(columns);

        // Set search bar options in a single call, filtering only clients with a name
        setOptions(
          partners
            .filter((partner) => partner.name)
            .map((partner) => ({ value: partner.id, label: partner.name }))
        );
        setLoadingRows(false);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
  };

  const handleTabChange = (newTab) => {
    setActivetab(newTab);

    const { rows, columns } = transformPartnersToTable(
      allPartners,
      allWorkspaces,
      newTab
    );

    setRows(rows);
    setColumns(columns);
  };

  // Fetch data when the component mounts or when currentUser changes
  useEffect(() => {
    if (currentUser) {
      handleFetchData();
    }
  }, [currentUser]);

  // If the data is still loading or the user is not authenticated, show a loading message
  if (loading || !currentUser || !userData || loadingRows) {
    return (
      <div className="h-full w-full pl-4 flex items-center justify-center flex-col gap-2">
        <SmallSpinner></SmallSpinner>
      </div>
    );
  }

  // Render the client data
  return (
    <Column align="left" className="h-full w-full pl-4">
      <Row align="left" className="w-full h-full max-w-c1p4k">
        <Column align="left" className="h-full w-full overflow-hidden" y={10}>
          <div className="text-hx font-medium">Partenaires</div>
          <Row align="left" className="w-full justify-between">
            {/* Search Bar */}
            <div className="bg-white border-1 border-shadow_25 rounded-full flex flex-row gap-2 items-center px-2 py-1">
              <Icon1 icon_name="search" size={20} fill="grey"></Icon1>
              <TFGPT1
                options={options}
                placeholder="Chercher"
                inputValue={searchValue}
                setInputValue={setSearchValue}
                onAction={gotoClientName}
                className="w-full"
                start_only={false}
              ></TFGPT1>
            </div>
            <div className="flex flex-row gap-2">
              <SecondaryButton big={true} onClick={() => setExtractOpen(true)}>
                <div className="flex flex-row content-center items-center">
                  <Icon1
                    icon_name="excel"
                    height="16px"
                    width="16px"
                    fill="grey"
                  ></Icon1>
                  <Space x={5} />
                  Extraire
                </div>
              </SecondaryButton>
              <PrimaryButton big={true} onClick={gotoNewClient}>
                <div className="flex flex-row content-center items-center ">
                  <Icon1
                    icon_name="plus"
                    height="16px"
                    width="16px"
                    fill="white"
                  ></Icon1>
                  <Space x={5} />
                  Nouveau Partenaire
                </div>
              </PrimaryButton>
            </div>
          </Row>
          <div className="flex flex-row gap-2">
            <div className="ml-0 pl-0 left-0 relative">
              <TabGPT
                tabs={tabs}
                activeTab={activetab}
                setActiveTab={handleTabChange}
                
              />
            </div>
          </div>

          {/* DataGrid displaying the clients */}
          <div
            style={{ padding: "0px" }}
            className="w-full h-full max-w-c1p4k overflow-y-auto"
          >
            {allPartners.length === 0 && !loadingRows && (
              <EmptyDisplay1
                title="Invitez votre premier partenaire"
                subtitle="Les partenaires vont collaborer avec vous sur vos dossiers !"
                goAction={gotoNewClient}
                actionText="Nouveau Partenaire"
              ></EmptyDisplay1>
            )}
            {allPartners.length > 0 && (
              <div className="h-full pb-10 min-h-6">
                <DataGridGPT2
                  rows={rows}
                  selectable={false}
                  columns={columns}
                  onClick={gotoClient}
                  initial_sort_field="name"
                  initial_sort_order="asc"
                ></DataGridGPT2>{" "}
              </div>
            )}
          </div>
        </Column>

        <Divider orientation="vertical" />
      </Row>
      <ExtrairePartnersModal
        isOpen={extractOpen}
        onClose={() => setExtractOpen(false)}
        partners={allPartners}
        workspaces={allWorkspaces}
        orgData={orgData}
        defaultPrenom={(userData?.display_name || "").split(" ")[0]}
      />
    </Column>
  );
}

export default Partners;
